import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '@/lib/api-client';
import {
  taskSchema,
  tasksListSchema,
  taskMutationResponseSchema,
  type TaskInput,
} from '../tasks-schemas';

const keys = {
  oportunidade: (id: string | null) => ['crm', 'tasks', 'oportunidade', id ?? ''] as const,
  minhas: () => ['crm', 'tasks', 'minhas'] as const,
  contagem: () => ['crm', 'tasks', 'contagem'] as const,
};

export const tasksContagemSchema = z.object({
  atrasadas: z.coerce.number().default(0),
  hoje: z.coerce.number().default(0),
  proximos_7_dias: z.coerce.number().default(0),
  sem_prazo: z.coerce.number().default(0),
  total: z.coerce.number().default(0),
});
export type TasksContagem = z.infer<typeof tasksContagemSchema>;

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['crm', 'tasks'] });
}

export function useTasksDaOportunidade(id: string | null) {
  return useQuery({
    queryKey: keys.oportunidade(id),
    queryFn: ({ signal }) =>
      api(`/api/v2/crm/${id}/tasks`, { schema: tasksListSchema, signal }),
    enabled: !!id,
    staleTime: 15_000,
  });
}

export function useMinhasTasks() {
  return useQuery({
    queryKey: keys.minhas(),
    queryFn: ({ signal }) =>
      api('/api/v2/crm/tasks/minhas', { schema: tasksListSchema, signal }),
    staleTime: 30_000,
  });
}

// Polling discreto: refresh a cada 60s. Usado pra badge global no shell.
export function useTasksContagem() {
  return useQuery({
    queryKey: keys.contagem(),
    queryFn: ({ signal }) =>
      api('/api/v2/crm/tasks/contagem', { schema: tasksContagemSchema, signal }),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useCriarTask(oportunidadeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TaskInput) =>
      api(`/api/v2/crm/${oportunidadeId}/tasks`, {
        method: 'POST',
        body: input,
        schema: taskMutationResponseSchema,
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useAtualizarTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, input }: { taskId: number; input: Partial<TaskInput> }) =>
      api(`/api/v2/crm/tasks/${taskId}`, {
        method: 'PUT',
        body: input,
        schema: taskMutationResponseSchema,
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useCancelarTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: number) =>
      api(`/api/v2/crm/tasks/${taskId}`, {
        method: 'DELETE',
        schema: taskMutationResponseSchema,
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

// Re-export pra fica acessível pro componente
export { taskSchema };
